import { ChevronDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { TAgentToolMessage } from '@/services/types/agent-chat-types';

import { BaseBadge } from './BaseBadge';
import { BaseToolBadgeProps } from './types';

const FileSchema = z.object({
  file_name: z.string(),
  artifact_id: z.string(),
  description: z.string().optional().nullable(),
});
const ListFilesResponseSchema = z.array(FileSchema);

type FileItemProps = {
  file: z.infer<typeof FileSchema>;
};

const FileItem = ({ file }: FileItemProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggle = () => setIsExpanded(!isExpanded);

  return (
    <div
      role="button"
      tabIndex={0}
      className="group flex flex-col gap-1.5 rounded-md p-2 hover:bg-muted/50 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={handleToggle}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleToggle();
        }
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs text-foreground">{file.file_name}</span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-180',
          )}
          aria-hidden="true"
        />
      </div>
      <div
        className={cn(
          'grid transition-all duration-200 ease-in-out',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <p className="text-xs text-muted-foreground py-1">
            {file.description || 'No description available'}
          </p>
        </div>
      </div>
    </div>
  );
};

type FileListProps = {
  files: z.infer<typeof FileSchema>[];
};

const FileList = ({ files }: FileListProps) => {
  return (
    <div className="flex flex-col divide-y divide-border">
      {files.map((file) => (
        <FileItem key={file.artifact_id} file={file} />
      ))}
    </div>
  );
};

const getFileContent = (toolMessage: TAgentToolMessage) => {
  console.log(toolMessage.additional_kwargs);
  if (toolMessage.additional_kwargs?.files) {
    return ListFilesResponseSchema.parse(toolMessage.additional_kwargs.files);
  }

  if (typeof toolMessage.content !== 'string') {
    return [];
  }
  const parsed = JSON.parse(toolMessage.content);
  const validated = ListFilesResponseSchema.safeParse(parsed);
  if (validated.success) {
    return validated.data;
  }
  return [];
};

console.log('ListFilesBadge');
export const ListFilesBadge = ({ isDone, ...props }: BaseToolBadgeProps) => {
  const allProps = { ...props, isDone };

  const files = useMemo(() => {
    if (!props.toolMessage) return [];
    try {
      return getFileContent(props.toolMessage);
    } catch (error) {
      return [];
    }
  }, [props.toolMessage]);

  const baseBadge = (
    <BaseBadge
      {...allProps}
      showArgs={false}
      resultTexts={{
        loading: 'Searching for files...',
        done:
          files.length > 0 ? `Found ${files.length} files` : 'No files found',
        error: 'Error searching for files',
      }}
    />
  );

  if (files.length === 0) {
    return baseBadge;
  }

  return (
    <HoverCard openDelay={0} closeDelay={200}>
      <HoverCardTrigger asChild>
        <div>{baseBadge}</div>
      </HoverCardTrigger>
      <HoverCardContent side="right" align="start" className="w-[450px] p-0">
        <div className="max-h-[300px] overflow-y-auto scrollbar py-3">
          <FileList files={files} />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};
